import {
  createContext, useContext, useEffect, useState, useMemo,
} from 'react';
import type { ReactNode } from 'react';
import { useProducts } from './ProductContext';
import { SALES_TAX_RATE } from '@bidetny/config';
import { supabase } from '../utils/supabaseClient';
import _ from 'lodash';
import type {
  CustomerToiletShape,
  ModelWarningAcknowledgement,
  ModelWarningFlag,
} from '../../shared/modelSelectionWarnings';
import type {
  BathroomPhotoClientAnalysis,
  PhotoVerificationOverride,
} from '../../shared/bathroomPhotoAnalysis';
import type {
  BuildingAccessFacts,
  BuildingDocumentAnalysisResponse,
  BuildingGovernance,
  BuildingScreeningResolution,
  LicensedPlumberAnswer,
} from '../../shared/buildingScreening';

const SYSTEM_SETTINGS_ID = '00000000-0000-0000-0000-000000000001';

export interface DiscountDetails {
  id: string;
  Code: string;
  discountAmount: number;
  [key: string]: any;
}

export interface ToiletMediaUpload {
  storagePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  analysis?: BathroomPhotoClientAnalysis;
}

export interface OrderItem {
  productId: string;
  variationId?: string;
  model: string;
  toiletMedia?: ToiletMediaUpload[];
  customerToiletShape?: CustomerToiletShape;
  photoVerificationOverride?: PhotoVerificationOverride;
  redFlagsAcknowledged?: boolean;
  photoRedFlagFollowUp?: string;
}

export interface OrderItemPriceBreakdown {
  bidetPrice: number;
  bidetTax: number;
  installFee: number;
  installTax: number;
  bidetTitle: string;
}

export interface OrderPriceBreakdown {
  discount?: DiscountDetails | null;
  items: OrderItemPriceBreakdown[];
  totalBidetPrice: number;
  totalBidetTax: number;
  totalInstallFee: number;
  totalInstallTax: number;
  total: number;
}

export interface OrderFormData {
  sessionId: string;
  quantity: number;
  items: OrderItem[];
  recipientName: string; // for install address
  firstName: string; // customer
  lastName: string; // customer
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  addressGooglePlace?: any;
  preferredDate: Date | null;
  email: string;
  phone: string;
  agreedToTerms: boolean;
  paymentIntent?: any; // Stripe PaymentIntent object
  priceBreakdown: OrderPriceBreakdown;
  discountCode?: string;
  modelWarningAcknowledgements: ModelWarningAcknowledgement[];
  modelWarningFlags: ModelWarningFlag[];
  buildingGovernance?: BuildingGovernance;
  licensedPlumberAnswer?: LicensedPlumberAnswer;
  buildingScreeningResolution?: BuildingScreeningResolution;
  buildingDocumentId?: string;
  buildingDocumentAnalysis?: BuildingDocumentAnalysisResponse;
  installerInstructions: string;
  buildingAccessFacts: BuildingAccessFacts;
}

const defaultOrder: OrderFormData = {
  sessionId: crypto.randomUUID(),
  quantity: 1,
  items: [],
  recipientName: '',
  firstName: '',
  lastName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  addressGooglePlace: null,
  preferredDate: null,
  email: '',
  phone: '',
  agreedToTerms: false,
  priceBreakdown: {
    items: [],
    totalBidetPrice: 0,
    totalBidetTax: 0,
    totalInstallFee: 0,
    totalInstallTax: 0,
    total: 0,
  },
  discountCode: undefined,
  modelWarningAcknowledgements: [],
  modelWarningFlags: [],
  buildingGovernance: undefined,
  licensedPlumberAnswer: undefined,
  buildingScreeningResolution: undefined,
  buildingDocumentId: undefined,
  buildingDocumentAnalysis: undefined,
  installerInstructions: '',
  buildingAccessFacts: {
    doorman: false,
    accessType: null,
    floor: null,
  },
};

interface OrderContextType {
  order: OrderFormData;
  orderHydrated: boolean;
  setOrder: (order: OrderFormData) => void;
  updateOrder: (
    fields: Partial<OrderFormData> | ((current: OrderFormData) => Partial<OrderFormData>),
  ) => void;
  clearOrder: () => void;
  availableTimes: AvailableTime[];
  fetchAvailableTimes: () => Promise<void>;
  unavailableSlotModalOpen: boolean;
  setUnavailableSlotModalOpen: (open: boolean) => void;
  slotsByDay: Record<string, AvailableTime[]>;
  fetchingAvailableTimes: boolean;
  // BidetModal state
  bidetModalOpen: boolean;
  selectedBidetIdForBidetModal: string | null;
  openBidetModal: (bidetId: string) => void;
  closeBidetModal: () => void;
  removeDiscountCode: () => void;
  // Site-wide ordering kill-switch (admin-controlled via /admin/system).
  // null while the flag is being fetched on mount; true / false once known.
  // Components that route to /order should check this and prefer
  // /ordering-paused when it's `false` so the user gets immediate feedback
  // instead of waiting on the loader-redirect chain.
  orderingEnabled: boolean | null;
}

export interface AvailableTime {
  installerId: string;
  start: string; // ISO string
  end: string; // ISO string
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

// All times are grouped by Eastern Time date (America/New_York)
const TIMEZONE = 'America/New_York';

// Get YYYY-MM-DD in Eastern Time from any ISO string
function getEasternDateKey(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-CA', { timeZone: TIMEZONE });
}

export function useOrder() {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error('useOrder must be used within OrderProvider');
  return ctx;
}

/**
 * Returns the path that an "order" CTA should navigate to right now.
 * Routes to '/ordering-paused' when the admin has flipped the kill-switch
 * off, otherwise '/order'. Use this anywhere a button or link sends the
 * user into the order flow so they get instant feedback rather than
 * waiting on the loader-redirect chain.
 */
export function useOrderRoute(): string {
  const { orderingEnabled } = useOrder();
  return orderingEnabled === false ? '/ordering-paused' : '/order';
}

function migrateOldOrderFormat(parsed: any): any {
  // Migrate old format: single productId/variationId -> items array
  if (parsed.productId && !parsed.items) {
    parsed.quantity = 1;
    parsed.items = [{
      productId: parsed.productId,
      variationId: parsed.variationId || '',
      model: parsed.model || '',
    }];
    delete parsed.productId;
    delete parsed.variationId;
    delete parsed.model;
    // Migrate old priceBreakdown format
    if (parsed.priceBreakdown && !parsed.priceBreakdown.items) {
      parsed.priceBreakdown = {
        items: [{
          bidetPrice: parsed.priceBreakdown.bidetPrice || 0,
          bidetTax: parsed.priceBreakdown.bidetTax || 0,
          installFee: parsed.priceBreakdown.installFee || 0,
          installTax: parsed.priceBreakdown.installTax || 0,
          bidetTitle: parsed.model || '',
        }],
        totalBidetPrice: parsed.priceBreakdown.bidetPrice || 0,
        totalBidetTax: parsed.priceBreakdown.bidetTax || 0,
        totalInstallFee: parsed.priceBreakdown.installFee || 0,
        totalInstallTax: parsed.priceBreakdown.installTax || 0,
        total: parsed.priceBreakdown.total || 0,
        discount: parsed.priceBreakdown.discount || null,
      };
    }
  }
  return parsed;
}

export function OrderProvider({ children }: { children: ReactNode }) {
  const { products: bidets } = useProducts();
  // SSR and the first client render must use the same state. Restore the
  // browser-only draft after mount so React hydrates deterministic markup.
  const [order, setOrder] = useState<OrderFormData>(defaultOrder);
  const [orderHydrated, setOrderHydrated] = useState(false);

  useEffect(() => {
    const cached = localStorage.getItem('order-in-progress');
    if (cached) {
      try {
        const parsed = migrateOldOrderFormat(JSON.parse(cached));
        setOrder({
          ...defaultOrder,
          ...parsed,
          preferredDate: parsed.preferredDate ? new Date(parsed.preferredDate) : null,
          addressLine1: parsed.addressLine1 || '',
          addressLine2: parsed.addressLine2 || '',
        });
      } catch {
        // An unreadable draft should not strand checkout on its loading state.
        // Persistence below replaces it with the current default format.
      }
    }
    setOrderHydrated(true);
  }, []);

  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);
  const [unavailableSlotModalOpen, setUnavailableSlotModalOpen] = useState(false);
  const [fetchingAvailableTimes, setFetchingAvailableTimes] = useState(false);

  // BidetModal state
  const [bidetModalOpen, setBidetModalOpen] = useState(false);
  const [selectedBidetIdForBidetModal, setselectedBidetIdForBidetModal] = useState<string | null>(null);

  // Ordering kill-switch flag — fetched once on mount.
  const [orderingEnabled, setOrderingEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('system_settings')
      .select('ordering_enabled')
      .eq('id', SYSTEM_SETTINGS_ID)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          // Fail open — the /order route loader and the order-creation
          // lambdas re-check the flag, so a missed read here just means
          // the user might briefly land on /order before getting redirected.
          setOrderingEnabled(true);
          return;
        }
        setOrderingEnabled(Boolean((data as { ordering_enabled?: boolean }).ordering_enabled));
      });
    return () => { cancelled = true; };
  }, []);

  const fetchAvailableTimes = async () => {
    setFetchingAvailableTimes(true);
    try {
      const res = await fetch(`/.netlify/functions/getAvailableInstallTimes`);
      if (!res.ok) throw new Error('Failed to fetch available times');
      const data = await res.json();
      setAvailableTimes(data);
    } catch (e) {
      setAvailableTimes([]);
    }
    setFetchingAvailableTimes(false);
  };

  useEffect(() => {
    fetchAvailableTimes();
  }, []);

  // Group available times by day (YYYY-MM-DD in Eastern Time)
  const slotsByDay = useMemo(() => availableTimes.reduce((acc, slot) => {
    const day = getEasternDateKey(slot.start); // Use Eastern Time, not UTC
    if (!acc[day]) acc[day] = [];
    acc[day].push(slot);
    return acc;
  }, {} as Record<string, typeof availableTimes>), [availableTimes]);

  // Check if selected slot is still available after fetching times
  useEffect(() => {
    if (!fetchingAvailableTimes && Object.keys(slotsByDay).length && order.preferredDate) {
      const dayKey = getEasternDateKey(order.preferredDate.toISOString()); // Use Eastern Time
      const slotAvailable = (slotsByDay[dayKey] || []).some((s) => order.preferredDate && new Date(s.start).getTime() === order.preferredDate.getTime());
      if (!slotAvailable) {
        setUnavailableSlotModalOpen(true);
        updateOrder({ preferredDate: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingAvailableTimes, setUnavailableSlotModalOpen, slotsByDay]);

  useEffect(() => {
    if (!orderHydrated) return;
    localStorage.setItem('order-in-progress', JSON.stringify(order));
  }, [order, orderHydrated]);


  async function updatePriceBreakdown() {
    try {
      console.log('Updating price breakdown');

      if (order.items.length === 0) {
        console.log('no bidets selected so resetting price breakdown');
        setOrder((prev) => ({
          ...prev,
          priceBreakdown: {
            ..._.cloneDeep(defaultOrder.priceBreakdown),
          },
        }));
        return;
      }

      // Fetch discount details once if discount code exists
      let discountDetails: DiscountDetails | null = null;
      if (order.discountCode) {
        const res = await fetch(`/.netlify/functions/getDiscountCode?code=${encodeURIComponent(order.discountCode)}`);
        if (!res.ok) {
          console.error('Failed to fetch discount code details');
        } else {
          discountDetails = await res.json();
        }
      }

      const itemBreakdowns: OrderItemPriceBreakdown[] = [];

      for (const item of order.items) {
        const selectedBidet = bidets.find((b) => b.id === item.productId);
        if (!selectedBidet) continue;

        let selectedVariation = null;
        if (item.variationId) {
          selectedVariation = selectedBidet.variations?.find(v => v.id === item.variationId);
        } else if (selectedBidet.variations) {
          selectedVariation = selectedBidet.variations.find(v => v.isDefaultVariation) || selectedBidet.variations[0];
        }

        // Catalog-rebuild-v2: fall back to MSRP when no reseller link is
        // populated for this variation. The new catalog has msrp on every
        // variation but reseller-link rows are seeded separately.
        const bidetPrice = selectedVariation
          ? (selectedVariation.resellerPrice || selectedVariation.msrp || 0)
          : 0;
        const bidetTax = bidetPrice * SALES_TAX_RATE;

        // Dynamic install fee logic. Catalog-rebuild-v2: legacy
        // category='bidet installation' is now product_type='installation_service'.
        // Display key 'Category' on the mapped Product object holds the new
        // product_type enum value (see app/data/productService.ts).
        const installProductVariation =
          bidets
            ?.find((p) => p.Category === 'installation_service' && p['Product ID'] === selectedBidet?.['Installation Type'])
            ?.variations.find(v => v.isDefaultVariation);
        if (!installProductVariation) {
          throw new Error('install price cannot be determined for selected bidet');
        }

        let installFee = installProductVariation.msrp;
        if (discountDetails && typeof discountDetails['Discount Code Amount'] === 'number') {
          installFee = Math.round((installFee - installFee * (discountDetails['Discount Code Amount'] / 100)) * 100) / 100;
        }
        const installTax = installFee * SALES_TAX_RATE;

        itemBreakdowns.push({
          bidetPrice,
          bidetTax,
          installFee,
          installTax,
          bidetTitle: selectedBidet.Title || selectedBidet['Model Name'] || '',
        });
      }

      const totalBidetPrice = itemBreakdowns.reduce((s, i) => s + i.bidetPrice, 0);
      const totalBidetTax = itemBreakdowns.reduce((s, i) => s + i.bidetTax, 0);
      const totalInstallFee = itemBreakdowns.reduce((s, i) => s + i.installFee, 0);
      const totalInstallTax = itemBreakdowns.reduce((s, i) => s + i.installTax, 0);
      const total = totalBidetPrice + totalBidetTax + totalInstallFee + totalInstallTax;

      setOrder((prev) => ({
        ...prev,
        priceBreakdown: {
          discount: discountDetails,
          items: itemBreakdowns,
          totalBidetPrice,
          totalBidetTax,
          totalInstallFee,
          totalInstallTax,
          total,
        },
      }));
    }
    catch (error) {
      console.error('Error updating price breakdown:', error);
      return;
    }
  }

  useEffect(() => {
    updatePriceBreakdown();
  }, [JSON.stringify(order.items), order.discountCode, bidets]);

  const updateOrder = (
    fields: Partial<OrderFormData> | ((current: OrderFormData) => Partial<OrderFormData>),
  ) => {
    setOrder((prev) => ({
      ...prev,
      ...(typeof fields === 'function' ? fields(prev) : fields),
    }));
  };

  const clearOrder = () => {
    setOrder({ ...defaultOrder, sessionId: crypto.randomUUID() });
    localStorage.removeItem('order-in-progress');
  };

  const openBidetModal = (bidetId: string) => {
    setselectedBidetIdForBidetModal(bidetId);
    setBidetModalOpen(true);
  };

  const closeBidetModal = () => {
    setselectedBidetIdForBidetModal(null);
    setBidetModalOpen(false);
  };

  const removeDiscountCode = () => {
    updateOrder({ discountCode: undefined });
  }

  console.log('current order in context', order);

  const context = {
    order,
    orderHydrated,
    slotsByDay,
    availableTimes,
    fetchingAvailableTimes,
    unavailableSlotModalOpen,
    bidetModalOpen,
    selectedBidetIdForBidetModal,
    orderingEnabled,

    setOrder,
    updateOrder,
    clearOrder,
    fetchAvailableTimes,
    setUnavailableSlotModalOpen,
    openBidetModal,
    closeBidetModal,
    removeDiscountCode,
  };

  return (
    <OrderContext.Provider value={context}>
      {children}
    </OrderContext.Provider>
  );
}
